import { Helmet } from "react-helmet-async";
import { defaultSEOConfig, type SEOConfig } from "./seo-config";

interface SEOProps extends Partial<SEOConfig> {
  children?: React.ReactNode;
}

export function SEO({
  title,
  description,
  canonical,
  openGraph,
  twitter,
  children,
}: SEOProps) {
  const seo = {
    title: title || defaultSEOConfig.title,
    titleTemplate: defaultSEOConfig.titleTemplate,
    description: description || defaultSEOConfig.description,
    canonical: canonical,
    openGraph: {
      ...defaultSEOConfig.openGraph,
      ...openGraph,
      title: openGraph?.title || title || defaultSEOConfig.openGraph.title,
      description: openGraph?.description || description || defaultSEOConfig.openGraph.description,
    },
    twitter: {
      ...defaultSEOConfig.twitter,
      ...twitter,
    },
  };

  return (
    <Helmet>
      {/* General tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Favicon tags */}
      <link rel="icon" href="/favicon.ico" sizes="any" />
      <link rel="icon" type="image/svg+xml" href="/images/brand/logo.svg" />
      <link rel="apple-touch-icon" sizes="180x180" href="/images/brand/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/images/brand/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/images/brand/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="theme-color" content="#18181b" />

      {/* OpenGraph tags */}
      <meta property="og:type" content={seo.openGraph.type} />
      <meta property="og:title" content={seo.openGraph.title} />
      <meta property="og:description" content={seo.openGraph.description} />
      <meta property="og:url" content={seo.openGraph.url} />
      <meta property="og:site_name" content={seo.openGraph.siteName} />
      {seo.openGraph.images?.map((image, index) => (
        <meta
          key={`og:image:${index}`}
          property="og:image"
          content={image.url}
        />
      ))}

      {/* Twitter Card tags */}
      <meta name="twitter:card" content={seo.twitter.cardType} />
      <meta name="twitter:site" content={seo.twitter.site} />
      <meta name="twitter:creator" content={seo.twitter.handle} />
      <meta name="twitter:title" content={seo.openGraph.title} />
      <meta name="twitter:description" content={seo.openGraph.description} />
      {seo.openGraph.images?.[0] && (
        <meta name="twitter:image" content={seo.openGraph.images[0].url} />
      )}

      {children}
    </Helmet>
  );
} 